/**
 * Workspace tabs state and its persistence (SPEC §17.3, §1A.3; owner 2026-09-27): the pure
 * `WorkspacesDoc` rules (workspaces.ts), cached in local storage for an instant start and synced as
 * the versioned document `layout/desktop` (form_factor desktop). `layout` is a whole-document type:
 * on a 409 the server copy wins and is loaded here.
 *
 * `revision` changes whenever the dock must be rebuilt from the document (switching workspace,
 * reset, a layout loaded from another device); ordinary drags only record the new layout.
 * Storage and the document client are injected so the round trip is unit-tested.
 *
 * Link-group symbols (GPT086): the window-wide group store is saved in the same document (`links`)
 * whenever this window retargets a group, and restored when the window starts — from the local
 * cache before the first render, then from the initial server load. A later document from another
 * device never retargets a warm window's groups (its next retarget saves this window's map).
 */
import { linkSymbolsOf, restoreLinkSymbols, sameLinkSymbols, type LinkGroupsApi } from "./linkGroups.ts";
import { DocSync } from "./sync/client.ts";
import {
  activeWorkspace,
  loadWorkspaces,
  parseJsonOrUndefined,
  sanitizeWorkspaces,
  setLinkSymbols,
  setWorkspaceLayout,
  type SerializedLayout,
  type WorkspacesDoc,
} from "./workspaces.ts";

export const LAYOUT_DOC_ID = "desktop";

export type WorkspaceSnapshot = { doc: WorkspacesDoc; revision: number };

/** localStorage-like; failures (quota, private mode) must not throw. */
export type KeyValueStorage = { get(key: string): string | null; set(key: string, value: string): void };

type SyncMeta = { version: number; dirty: boolean };

// JSONB/wire object-key order is not a layout change. Keep JSON's existing treatment of
// undefined values, but retain array order and every persisted value exactly.
const orderedJson = (value: unknown): string | undefined =>
  JSON.stringify(value, (_key, item: unknown): unknown => {
    if (item === null || typeof item !== "object" || Array.isArray(item)) return item;
    const record = item as Record<string, unknown>;
    return Object.fromEntries(
      Object.keys(record)
        .sort()
        .map((key) => [key, record[key]]),
    );
  });
const sameJson = (a: unknown, b: unknown): boolean => orderedJson(a) === orderedJson(b);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** Only the initial fromJSON fit may change these viewport pixels without a user edit. */
function restoredContent(layout: SerializedLayout): SerializedLayout {
  const grid = layout["grid"];
  if (!isRecord(grid)) return layout;
  const nodeContent = (node: unknown): unknown => {
    if (!isRecord(node)) return node;
    const branch = node["type"] === "branch" && Array.isArray(node["data"]);
    const leaf = node["type"] === "leaf" && isRecord(node["data"]);
    if (!branch && !leaf) return node;
    const next = { ...node };
    if (typeof next["size"] === "number" && Number.isFinite(next["size"]) && next["size"] >= 0)
      next["size"] = 0;
    if (branch) next["data"] = (node["data"] as unknown[]).map(nodeContent);
    return next;
  };
  const next: Record<string, unknown> = { ...grid, root: nodeContent(grid["root"]) };
  for (const key of ["width", "height"])
    if (typeof next[key] === "number" && Number.isFinite(next[key]) && next[key] >= 0) next[key] = 0;
  return { ...layout, grid: next };
}

export class WorkspacesStore {
  private snap: WorkspaceSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly key: string;
  private readonly storage: KeyValueStorage;
  private readonly sync: boolean;
  private readonly docs: DocSync;
  private readonly template: string;
  private readonly saveDelayMs: number;
  private meta: SyncMeta;
  /** Server body at `meta.version` (the If-Match base). */
  private base: WorkspacesDoc | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private saving: Promise<void> | null = null;
  private started = false;
  private starting: Promise<void> | null = null;
  private readonly freshLocal: boolean;
  private firstRun = false;
  private seenServerWorkspace = false;
  private readonly linkGroups: LinkGroupsApi | null;

  constructor(opts: {
    storageKey: string;
    /** Where the single layout was saved before workspaces (migrated once). */
    legacyKey: string | null;
    storage: KeyValueStorage;
    /** Sync with the server document (the real desktop window; never the dev preview). */
    sync: boolean;
    template: string;
    newId: () => string;
    docs?: DocSync;
    saveDelayMs?: number;
    /** The window's link groups (`useLinkGroups`), saved and restored with the document. */
    linkGroups?: LinkGroupsApi;
  }) {
    this.key = opts.storageKey;
    this.storage = opts.storage;
    this.sync = opts.sync;
    this.docs = opts.docs ?? new DocSync();
    this.template = opts.template;
    this.saveDelayMs = opts.saveDelayMs ?? 1500;
    const loaded = loadWorkspaces({
      stored: parseJsonOrUndefined(this.storage.get(this.key)),
      legacy: opts.legacyKey ? parseJsonOrUndefined(this.storage.get(opts.legacyKey)) : undefined,
      template: opts.template,
      newId: opts.newId(),
    });
    const meta = parseJsonOrUndefined(this.storage.get(`${this.key}:sync`)) as Partial<SyncMeta> | undefined;
    // Even malformed cached content is not an invitation to treat this as a new user.
    this.freshLocal =
      this.storage.get(this.key) === null &&
      (opts.legacyKey === null || this.storage.get(opts.legacyKey) === null);
    this.meta = {
      version: typeof meta?.version === "number" ? meta.version : 0,
      // A migrated or fresh document has never been saved to the server.
      dirty: loaded.source !== "stored" || meta?.dirty === true,
    };
    this.snap = { doc: loaded.doc, revision: 0 };
    if (loaded.source !== "stored") this.persistLocal();
    this.linkGroups = opts.linkGroups ?? null;
    if (this.linkGroups) this.bindLinkGroups(this.linkGroups);
  }

  /** Restore the cached groups now (before any panel renders), then save every retarget. */
  private bindLinkGroups(groups: LinkGroupsApi): void {
    restoreLinkSymbols(groups, this.snap.doc.links);
    let last = linkSymbolsOf(groups.getState().groups);
    groups.subscribe((state) => {
      const next = linkSymbolsOf(state.groups);
      if (sameLinkSymbols(next, last)) return;
      last = next;
      this.update((d) => setLinkSymbols(d, next));
    });
  }

  // ---- subscription (React: useSyncExternalStore) -------------------------------------------

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  getSnapshot = (): WorkspaceSnapshot => this.snap;

  get doc(): WorkspacesDoc {
    return this.snap.doc;
  }

  /** Server version this window last saw (0 = never synced). */
  get syncedVersion(): number {
    return this.meta.version;
  }

  get dirty(): boolean {
    return this.meta.dirty;
  }

  /** A successful initial load found neither local/legacy nor server workspaces. */
  get firstRunEligible(): boolean {
    return this.firstRun;
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  // ---- mutations ---------------------------------------------------------------------------

  /**
   * Apply a pure change. `rebuild` when the dock must reload from the document; by default only
   * when the active workspace changed.
   */
  update(change: (d: WorkspacesDoc) => WorkspacesDoc, opts: { rebuild?: boolean } = {}): void {
    const next = change(this.snap.doc);
    if (next === this.snap.doc) return;
    const rebuild = opts.rebuild ?? next.activeId !== this.snap.doc.activeId;
    this.snap = { doc: next, revision: this.snap.revision + (rebuild ? 1 : 0) };
    this.changed();
  }

  /** Record the dock's current layout (no rebuild); observation baselines belong to that dock. */
  captureLayout(
    id: string,
    layout: SerializedLayout,
    observed: { previous?: SerializedLayout; restoredFrom?: SerializedLayout } = {},
  ): void {
    const cur = this.snap.doc.workspaces.find((w) => w.id === id);
    if (!cur || sameJson(cur.layout, layout)) return;
    if (observed.previous && sameJson(observed.previous, layout)) return;
    // Compare against the original saved input, not its migrated copy, so migrations and
    // Dockview content repairs still persist. This exception never applies to later sash edits.
    if (observed.restoredFrom && sameJson(restoredContent(observed.restoredFrom), restoredContent(layout)))
      return;
    let doc = setWorkspaceLayout(this.snap.doc, id, layout);
    // A template's groups may already be seeded when an initial server document replaces the
    // local one. Record those actual groups with its first layout even without a new retarget.
    // Keep explicit server links (startup restoration may still be pending), and never infer
    // groups when merely editing an older saved layout that has no map.
    if (cur.layout === null && doc.links === undefined && this.linkGroups) {
      doc = setLinkSymbols(doc, linkSymbolsOf(this.linkGroups.getState().groups));
    }
    this.snap = { doc, revision: this.snap.revision };
    this.changed();
  }

  private changed(): void {
    this.meta = { ...this.meta, dirty: true };
    this.persistLocal();
    this.emit();
    this.scheduleSave();
  }

  private persistLocal(): void {
    this.storage.set(this.key, JSON.stringify(this.snap.doc));
    this.storage.set(`${this.key}:sync`, JSON.stringify(this.meta));
  }

  // ---- server sync -------------------------------------------------------------------------

  /** Load the synced document once per window: a newer server copy wins, else ours is pushed. */
  start(): Promise<void> {
    if (!this.sync) return Promise.resolve();
    if (this.starting) return this.starting;
    this.started = true;
    this.starting = this.loadInitial();
    return this.starting;
  }

  private async loadInitial(): Promise<void> {
    try {
      const server = await this.docs.load<Record<string, unknown>>("layout", LAYOUT_DOC_ID);
      if (server) {
        this.seenServerWorkspace = true;
        const body = sanitizeWorkspaces(server.body, this.template);
        if (body && (server.version > this.meta.version || !this.meta.dirty)) {
          this.adopt(body, server.version);
          return;
        }
        this.base = body;
        this.meta = { ...this.meta, version: server.version };
      } else {
        this.meta = { ...this.meta, version: 0 };
        this.firstRun = this.freshLocal && !this.seenServerWorkspace;
      }
      if (this.meta.dirty) await this.save();
    } catch {
      this.firstRun = false;
      // API unreachable: keep the local copy; the next change retries.
    } finally {
      // Start-up only: an adopted server copy's groups replace the cached / template ones.
      if (this.linkGroups) restoreLinkSymbols(this.linkGroups, this.snap.doc.links);
    }
  }

  /** A `DocChanged` for layout/desktop (another device, or our own save echoing back). */
  async refresh(version: number): Promise<void> {
    if (!this.sync || version <= this.meta.version) return;
    try {
      const server = await this.docs.load<Record<string, unknown>>("layout", LAYOUT_DOC_ID);
      if (!server || server.version <= this.meta.version) return;
      const body = sanitizeWorkspaces(server.body, this.template);
      if (body) this.adopt(body, server.version);
    } catch {
      /* the next change or reload catches up */
    }
  }

  private adopt(body: WorkspacesDoc, version: number): void {
    this.seenServerWorkspace = true;
    const hadInvitation = this.firstRun;
    this.firstRun = false;
    const cur = this.snap.doc;
    this.base = body;
    this.meta = { version, dirty: false };
    if (sameJson(cur, body)) {
      this.persistLocal();
      if (hadInvitation) {
        this.snap = { ...this.snap };
        this.emit();
      }
      return;
    }
    // Rebuild the dock only when what is on screen changed.
    const a = activeWorkspace(cur);
    const b = activeWorkspace(body);
    const rebuild =
      a.id !== b.id || !sameJson(a.layout, b.layout) || a.template !== b.template || a.locked !== b.locked;
    this.snap = { doc: body, revision: this.snap.revision + (rebuild ? 1 : 0) };
    this.persistLocal();
    this.emit();
  }

  private scheduleSave(): void {
    if (!this.sync || !this.started) return;
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.save();
    }, this.saveDelayMs);
    // Node (tests): a pending debounce must not keep the process alive.
    (this.saveTimer as { unref?: () => void }).unref?.();
  }

  /** Save now (debounced saves and page-hide call this). Concurrent calls share one request. */
  async save(): Promise<void> {
    if (!this.sync) return;
    if (this.saving) {
      await this.saving;
      if (this.meta.dirty) await this.save();
      return;
    }
    this.saving = this.saveOnce();
    try {
      await this.saving;
    } finally {
      this.saving = null;
    }
  }

  private async saveOnce(): Promise<void> {
    const local = this.snap.doc;
    try {
      const r = await this.docs.save("layout", LAYOUT_DOC_ID, this.base ?? local, this.meta.version, local, {
        formFactor: "desktop",
      });
      if (r.kind === "saved") {
        this.base = local;
        // Edits made while the request was in flight stay dirty and save next.
        this.meta = { version: r.doc.version, dirty: this.snap.doc !== local };
        this.persistLocal();
        if (this.meta.dirty) this.scheduleSave();
      } else {
        // Whole-document type: another device saved first; its copy wins (SPEC §1A.3).
        const body = sanitizeWorkspaces(r.server.body, this.template);
        if (body) this.adopt(body, r.server.version);
      }
    } catch {
      // Offline: stays dirty; retried on the next change or reload.
    }
  }

  /** Flush a pending debounced save (page hide). */
  flush(): void {
    if (this.saveTimer === null) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    void this.save();
  }
}
